// Creating deals. Contact-first: a deal needs only a type, a name and a person.
// The deal type's fee template is copied onto the new deal; editing the deal never
// changes the template.

import type { FeeTemplate } from './commission';
import { DEAL_TYPES, STAGES, type DealType } from './commission/stages';
import { supabase, unwrap, unwrapMaybe } from './supabase';

export const DEAL_TYPE_LABEL: Record<string, string> = Object.fromEntries(DEAL_TYPES.map((d) => [d.key, d.label]));

export function isLeaseDeal(t: string): boolean {
  return t === 'tenant_rep' || t === 'll_rep';
}

export async function createDeal(input: {
  dealType: DealType;
  name: string;
  primaryContactId: string;
  orgId?: string | null;
  buildingId?: string | null;
}): Promise<string> {
  const stages = unwrap(await supabase.from('deal_stages').select('stage_key').eq('deal_type', input.dealType).order('sort_order').limit(1));
  const firstStage = stages[0]?.stage_key ?? STAGES[input.dealType][0]!.key;
  const deal = unwrap(
    await supabase
      .from('deals')
      .insert({
        deal_type: input.dealType,
        stage: firstStage,
        name: input.name,
        primary_contact_id: input.primaryContactId,
        org_id: input.orgId ?? null,
        building_id: input.buildingId ?? null,
      })
      .select('id')
      .single(),
  );

  const template = unwrapMaybe(await supabase.from('fee_templates').select('components, payout').eq('deal_type', input.dealType).maybeSingle());
  if (template) {
    const t = template as unknown as FeeTemplate;
    if (t.components.length) {
      unwrap(
        await supabase.from('fee_components').insert(
          t.components.map((c, i) => ({
            deal_id: deal.id,
            component_key: c.key,
            label: c.label ?? null,
            sort_order: i,
            basis: c.basis,
            rate: c.rate,
            payer: c.payer,
          })),
        ),
      );
    }
    const p = t.payout;
    unwrap(
      await supabase.from('payout_structures').insert({
        deal_id: deal.id,
        kind: p.kind,
        cash_splits: p.kind === 'cash_out' ? (p.splits as never) : null,
        annuity_monthly_amount: p.kind === 'annuity' ? p.monthlyAmount : null,
        annuity_months: p.kind === 'annuity' ? p.months : null,
        annuity_trigger: p.kind === 'annuity' ? p.trigger : null,
      }),
    );
  }
  if (isLeaseDeal(input.dealType)) {
    unwrap(await supabase.from('deal_lease_terms').insert({ deal_id: deal.id }));
  }
  return deal.id;
}
